import { useBoolean } from '@fluentui/react-hooks';
import { createContext, useContext, useState } from 'react';

type AppNavigationContextType = {
  navigationSize: number;
  workspacesDisabled: boolean;
  setNavigationSize(size: number): void;
  disableWorkspacesDropdown(): void;
  enableWorkspacesDropdown(): void;
};

const SizeContext = createContext<AppNavigationContextType | undefined>(undefined);

export const AppNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [navigationSize, setNavigationSize] = useState<number>(0);
  // Pages are workspace-agnostic ("Global") by DEFAULT. The `RequireWorkspaces` route guard is the
  // single mechanism that opts a page in: it enables on mount and disables on unmount. Defaulting to
  // `true` means an unguarded page can never inherit an enabled switcher from the previously visited
  // page, so no page needs a compensating "disable me" call of its own.
  // The only legitimate direct callers are query-state-driven toggles INSIDE an already-guarded route
  // (an "all workspaces" view switch — see AuditTrailPage, AllRunsPage).
  const [workspacesDisabled, { setTrue: disableWorkspacesDropdown, setFalse: enableWorkspacesDropdown }] =
    useBoolean(true);

  return (
    <SizeContext.Provider
      value={{
        navigationSize,
        workspacesDisabled,
        setNavigationSize,
        disableWorkspacesDropdown,
        enableWorkspacesDropdown,
      }}
    >
      {children}
    </SizeContext.Provider>
  );
};

export const useAppNavigationContext = (): AppNavigationContextType => {
  const context = useContext(SizeContext);
  if (!context) {
    throw new Error('useSizeContext must be used within a SizeProvider');
  }
  return context;
};
